import { createApp } from "vue"; // Vue 앱 인스턴스 생성
import App from "./App.vue";
import router from "@/router"; // 라우터 등록
import vuetify from "@/common/plugins/vuetify"; // Vuetify 플러그인
import i18n from "@/i18n"; // 다국어(i18n) 설정
import global from "@/app/plugins/global"; // 전역 헬퍼/프로퍼티
import { createPinia } from "pinia"; // Pinia
import piniaPlugin from "pinia-plugin-persistedstate"; // Pinia 상태 영속 플러그인
import { setAxios } from "@/api/Axios"; // Axios 설정
import EasyDataTable from "@/common/plugins/easyTable"; // table 컴포넌트
import purifyPlugin from "@/common/plugins/dompurify"; // DOMPurify 플러그인 (XSS)

// 베이스 컴포넌트 객체
import { Base } from "@/common/base-components/BaseFactory.ts";

// 어드민 베이스 컴포넌트
import BaseBreadcrumb from "@/components/base/BaseBreadcrumb.vue";
import BaseCard from "@/components/base/BaseCard.vue";
import BaseSearch from "@/components/base/BaseSearch.vue";
import BaseTable from "@/common/base-components/admin/BaseTable.vue";

// 전역 스타일(Scss)
import "@/assets/scss/style.scss"; // 전역 스타일
import "@/assets/common/default.css"; // 공통 CSS

// MathLive (수식 입력 에디터) 폰트/스타일
import "mathlive/fonts.css";
import "mathlive/static.css";

// LMS 스타일
import "@/assets/common/css/lms.css";
import "@/assets/common/css/math-editor.css";
import "@/assets/common/css/lms-pages/auth.css";
import "@/assets/common/css/lms-pages/course.css";
import "@/assets/common/css/lms-pages/public-course.css";
import "@/assets/common/css/lms-pages/notice.css";
import "@/assets/common/css/lms-pages/message.css";
import "@/assets/common/css/lms-pages/member.css";
import "@/assets/common/css/lms-pages/community.css";
import "@/assets/common/css/lms-pages/assignment.css";
import "@/assets/common/css/lms-pages/assistant.css";
import "@/assets/common/css/lms-pages/student.css";
import "@/assets/common/css/lms-pages/mylearn.css";
import "@/assets/common/css/lms-pages/student-exam.css";
import "@/assets/common/css/lms-pages/admin-exam.css";
import "@/assets/common/css/lms-pages/sms.css";
import "@/assets/common/css/lms-pages/ecp.css";
// 수료과정: 전역 .ecp_detail_* / .extra_course_badge 를 화면별로 덮는 규칙이 있어
// 반드시 ecp.css·lms.css 뒤에 와야 한다 (기존 <style scoped> 의 우선순위를 재현)
import "@/assets/common/css/lms-pages/complete-course.css";
// 뒤로가기 버튼 통일 — 페이지별 CSS(public-course/member/assignment/ecp/…)를 모두 덮어야 하므로
// 반드시 '가장 마지막'에 로드한다
import "@/assets/common/css/lms-pages/back-button.css";

// router navigation
import { navigationGuard } from "@/router/NavigationGuard.ts";

// Pinia 인스턴스 생성
const pinia = createPinia(); // Pinia 스토어 생성
pinia.use(piniaPlugin); // 상태 영속화 플러그인 적용

// 앱 인스턴스 생성 및 전역 컴포넌트/플러그인 등록
const app = createApp(App); // 앱 생성

// 전역 컴포넌트 등록
app.component("BaseInput", Base.Input);
app.component("BaseButton", Base.Button);
app.component("VEasyTable", EasyDataTable);
app.component("BaseBreadcrumb", BaseBreadcrumb);
app.component("BaseCard", BaseCard);
app.component("BaseSearch", BaseSearch);
app.component("BaseTable", BaseTable);

// 전역 플러그인 사용
app.use(purifyPlugin); // DOM 정화 플러그인 등록
app.use(pinia); // Pinia 등록
app.use(setAxios); // Axios 인터셉터/기본설정 등록
app.use(i18n); // i18n 등록

// favicon 동적 설정 (i18n 로드 후 실행)
const setFavicon = (): void => {
  // pbl 모드에서는 기본 파비콘 유지
  if (import.meta.env.MODE === "pbl") {
    return;
  }

  const faviconPath = i18n.global.t("siteConfig.favicon");

  // 키값 자체가 반환되지 않았는지 확인
  if (faviconPath && faviconPath !== "siteConfig.favicon") {
    let faviconLink = document.querySelector('link[rel~="icon"]') as HTMLLinkElement;

    if (!faviconLink) {
      faviconLink = document.createElement("link");
      faviconLink.rel = "icon";
      document.head.appendChild(faviconLink);
    }

    faviconLink.href = `${faviconPath}?v=${new Date().getTime()}`;
  } else {
    console.warn("Favicon 경로를 찾을 수 없음:", faviconPath);
  }
};

setFavicon();

app.use(global); // 전역 헬퍼/프로퍼티 등록
app.use(router); // 라우터 등록 (라우트 사용 가능)
app.use(vuetify); // Vuetify UI 프레임워크 적용

// 앱 마운트 및 네비게이션 가드 초기화
app.mount("#app"); // DOM에 앱 마운트
navigationGuard(router); // 라우팅 가드 등록/초기화

// 재배포 직후 옛 index.html 이 사라진 청크를 dynamic import 하다 실패하는 경우 → 새 index.html 로 1회 새로고침.
// (정적 자산이 서버에서 제대로 서빙되는 전제. 서버가 .js 에 text/html 을 주는 배포 문제는 서버 설정으로 해결해야 함)
window.addEventListener("vite:preloadError", () => {
  const KEY = "vite-preload-reloaded";
  if (sessionStorage.getItem(KEY)) return; // 무한 새로고침 방지(1회만)
  sessionStorage.setItem(KEY, "1");
  window.location.reload();
});
router.afterEach(() => sessionStorage.removeItem("vite-preload-reloaded")); // 정상 진입 시 플래그 해제
